import I18n from 'i18n-js'
import { connect } from 'react-redux'
import { ChangeEvent, FC, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { Link, useLocation } from 'react-router-dom'
import * as SimpleForm from 'builder/components/SimpleForm'
import Checkbox from 'builder/components/Checkbox'
import { TextField } from 'builder/components/TextField'
import { Recaptcha } from 'builder/components/Recaptcha'
import { Icon20 } from 'builder/components/Icon'
import { DocumentTypes, FetchStatuses } from 'builder/modules/constants'
import { actions, SignUpInfo, SignUpStore } from 'builder/modules/signUp'
import { bindActionToPromise } from 'builder/utils/bindActionToPromise'
import { withMediaQueries } from 'builder/components/MediaQueries'
import { Container } from '../styles'
import {
  FieldSuggestion,
  FieldSuggestionLink,
  FieldSuggestionText,
  RecaptchaContainer,
  Title,
  WarningContainer,
  WarningIcon,
  WarningText,
} from '../ContactInfo/styles'
import SocialProfileButtons from '../SocialProfile/SocialProfileButtons'
import {
  DividerContainer,
  Footer,
  Line,
  OrText,
  SingleSignUpContent,
  SocialsContainer,
  WLSubtitle,
  WLTitle,
  Wrapper,
} from './styles'
import { ConfigScopesEnum } from 'builder/modules/init'
import { useConfig } from 'builder/hooks/useConfig'
import Button from 'builder/components/Button'
import { NAME_FIELD_REGEX } from './config'

type SingleSignUpState = {
  showThankYouBlock: boolean
  emailEmptyError: boolean
}

type SingleSignUpProps = {
  onBack: () => void
  emailErrorCode: string
  persistSignUpInfo: ({ type, ...props }: { type: string; [x: string]: string | boolean }) => void
  email: string
  confirmEmail: boolean
  mediaQueries: {
    hover: boolean
    isPhone: boolean
    isTablet: boolean
    isLaptop: boolean
    isDesktop: boolean
  }
  setSuggestedEmail: (value: string) => void
  setEmailErrorCode: (value: string) => void
  suggestedEmail: string
  validateEmail: Awaited<({ email }: { email: string }) => any>
  isEmailSuggested: boolean
  isEmailValidating: boolean
  signUpProcess: string
  documentType: string
  isThankYouPageVisible: boolean
  onContinue: () => void
  templateId: string
  firstNameError?: string
  lastNameError?: string
  firstName?: string
  lastName?: string
}

const SingleSignUp: FC<SingleSignUpProps> = props => {
  const location = useLocation()
  const config = useConfig(ConfigScopesEnum.signUp)
  const { disableFacebookLogin, disableGoogleLogin, disableLinkedinLogin } = config?.features ?? {
    disableFacebookLogin: false,
    disableGoogleLogin: false,
    disableLinkedinLogin: false,
  }

  const { signUpTitle, signUpSubtitle, whitelabeled } = config?.settings || { whitelabeled: false }

  const [componentState, setComponentState] = useState<SingleSignUpState>({
    showThankYouBlock: false,
    emailEmptyError: false,
  })

  // Only for whitelabeled: first name and last name fields
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
  })

  const {
    email,
    confirmEmail,
    mediaQueries,
    suggestedEmail,
    isEmailSuggested,
    isEmailValidating,
    signUpProcess,
    isThankYouPageVisible,
    documentType,
    emailErrorCode,
    setSuggestedEmail,
    setEmailErrorCode,
    persistSignUpInfo,
    validateEmail,
    onContinue,
    templateId,
    firstName,
    lastName,
    firstNameError,
    lastNameError,
  } = props

  const handleSuggestionClick = () => {
    persistSignUpInfo({
      type: documentType,
      email: suggestedEmail,
    })
    setSuggestedEmail('')

    trackInternalEvent('take_suggested_sign_up_email')
  }

  const emailError = () => {
    if (componentState.emailEmptyError) return I18n.t('builder.sign_up.field_is_required')

    if (emailErrorCode) {
      const logInLink = `<a href='/app/auth/sign-in'>${I18n.t('builder.sign_up.log_in')}</a>`

      return emailErrorCode === 'TAKEN'
        ? I18n.t('builder.sign_up.email_is_taken', { logInLink })
        : I18n.t('builder.sign_up.invalid_email')
    }

    return ''
  }

  const warningText = () => {
    const dividedSuggestion = suggestedEmail.split('@')
    const emailLogin = `${dividedSuggestion[0]}@`
    const emailDomain = dividedSuggestion[1]

    return (
      <FieldSuggestion onClick={handleSuggestionClick}>
        <FieldSuggestionText>{`${I18n.t('builder.sign_up.did_you_mean')}:`}</FieldSuggestionText>
        <FieldSuggestionLink>
          {emailLogin}
          <b>{emailDomain}</b>
        </FieldSuggestionLink>
      </FieldSuggestion>
    )
  }

  const warningMarkup = suggestedEmail ? warningText() : ''

  const handleContinueClick = () => {
    if (isThankYouPageVisible && !componentState.showThankYouBlock) {
      setComponentState({ ...componentState, showThankYouBlock: true })
      trackInternalEvent('saw_thankyou_signup')
      return
    }

    trackInternalEvent('submit_sign_up_email')

    if (!email) {
      trackInternalEvent('see_sign_up_email_error', { error: 'empty_email' })
      setComponentState({ ...componentState, emailEmptyError: true })
      return
    }

    if (confirmEmail) {
      // Track marketing consent submission event
      trackInternalEvent('submit_marketing_consent', {
        website_host: window.location.host,
        device: mediaQueries.isPhone ? 'mobile' : 'desktop',
        step: 'emailstep',
      })
    }

    return validateEmail({ email }).then(() => {
      trackInternalEvent('send_sign_up_email')
      onContinue()
    })
  }

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    persistSignUpInfo({
      type: documentType,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    })

    if (e.target.name === 'email') {
      setComponentState({ ...componentState, emailEmptyError: false })
      setEmailErrorCode('')
      setSuggestedEmail('')
    }
  }

  const updateField = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value.search(NAME_FIELD_REGEX) === -1) {
      persistSignUpInfo({
        type: documentType,
        [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : value,
      })
      handleFieldChange(e)

      setErrors({
        ...errors,
        [e.target.name]: '',
      })
    } else {
      setErrors({
        ...errors,
        [e.target.name]: I18n.t('builder.sign_up.emoji_error'),
      })
    }
  }

  return (
    <>
      <Wrapper>
        <Container>
          {whitelabeled ? (
            <>
              <WLTitle>{signUpTitle ?? I18n.t('builder.sign_up.create_my_account')}</WLTitle>
              {signUpSubtitle && <WLSubtitle>{signUpSubtitle}</WLSubtitle>}
            </>
          ) : (
            <Title>{I18n.t('builder.sign_up.create_my_account')}</Title>
          )}

          <SingleSignUpContent whitelabeled={whitelabeled}>
            <SimpleForm.Form
              style={{ order: whitelabeled ? 2 : 'unset' }}
              onSubmit={!whitelabeled ? handleContinueClick : undefined}
              submitButtonText={
                isEmailSuggested
                  ? I18n.t('builder.sign_up.confirm')
                  : I18n.t('builder.sign_up.continue')
              }
              singleSignFlow={true}
              isPending={isEmailValidating || signUpProcess === FetchStatuses.loading}
            >
              {!componentState.showThankYouBlock && (
                <>
                  <SimpleForm.Row style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {whitelabeled && (
                      <div
                        style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}
                      >
                        <TextField
                          name="firstName"
                          label={I18n.t('builder.sign_up.first_name')}
                          value={firstName}
                          onChange={updateField}
                          error={errors.firstName || firstNameError}
                          autoFocus={!mediaQueries.isPhone}
                          data-testid="first-name-input"
                          type="text"
                          errorDataTestId="first_name-error"
                        />
                        <TextField
                          name="lastName"
                          label={I18n.t('builder.sign_up.last_name')}
                          value={lastName}
                          onChange={updateField}
                          error={errors.lastName || lastNameError}
                          type="text"
                          autoFocus={!mediaQueries.isPhone}
                          data-testid="last-name-input"
                          errorDataTestId="last_name-error"
                        />
                      </div>
                    )}
                    <TextField
                      name="email"
                      type="email"
                      label={I18n.t('builder.sign_up.email')}
                      value={email}
                      onChange={handleFieldChange}
                      warning={warningMarkup}
                      error={emailError()}
                      autoFocus={!mediaQueries.isPhone}
                      data-testid="email-input"
                      errorDataTestId="email-error"
                    />
                    <Checkbox
                      label={I18n.t('builder.sign_up.consent_checkbox_label')}
                      checked={confirmEmail}
                      onChange={handleFieldChange}
                      name="confirm_email"
                    />
                  </SimpleForm.Row>
                </>
              )}
              {!componentState.showThankYouBlock && suggestedEmail && (
                <WarningContainer>
                  <WarningIcon>
                    <Icon20.WarningCircle />
                  </WarningIcon>

                  <WarningText>{I18n.t('builder.sign_up.you_made_typo')}</WarningText>
                </WarningContainer>
              )}
              {isThankYouPageVisible && componentState.showThankYouBlock && (
                <>
                  <SimpleForm.Row>
                    <Checkbox
                      label={I18n.t('builder.sign_up.consent_checkbox_label')}
                      checked={confirmEmail}
                      onChange={handleFieldChange}
                      name="confirm_email"
                    />
                  </SimpleForm.Row>
                </>
              )}
              <RecaptchaContainer>
                <Recaptcha />
              </RecaptchaContainer>
            </SimpleForm.Form>
            {(!disableFacebookLogin || !disableGoogleLogin || !disableLinkedinLogin) && (
              <SocialsContainer whitelabeled={whitelabeled}>
                <DividerContainer>
                  <Line />
                  <OrText>{I18n.t('builder.sign_up.or')}</OrText>
                  <Line />
                </DividerContainer>
                <SocialProfileButtons
                  templateId={templateId}
                  documentType={documentType}
                  hasSpacing={false}
                  singleSignFlow={true}
                  availableSocialNetworks={{
                    google: !disableGoogleLogin,
                    facebook: !disableFacebookLogin,
                    linkedin: !disableLinkedinLogin,
                  }}
                />
              </SocialsContainer>
            )}
            <Footer whitelabeled={whitelabeled}>
              <div style={{ margin: 'auto 0' }}>
                {I18n.t('builder.sign_up.already_have_account')}
                {' — '}
                <Link to={`/auth/sign-in${location.search}`}>
                  {I18n.t('builder.sign_up.sign_in')}
                </Link>
              </div>
              {whitelabeled && (
                <Button
                  isDisabled={isEmailValidating || signUpProcess === FetchStatuses.loading}
                  isLoading={isEmailValidating || signUpProcess === FetchStatuses.loading}
                  type="submit"
                  onClick={handleContinueClick}
                >
                  {I18n.t('builder.sign_in.continue')}
                </Button>
              )}
            </Footer>
          </SingleSignUpContent>
        </Container>
      </Wrapper>
    </>
  )
}

// ---
// Connect to data
// ---

const mapStateToProps = (state: { signUp: SignUpStore }) => ({
  emailErrorCode: state.signUp.emailErrorCode,
  email: state.signUp.signUpInfo.email,
  firstName: state.signUp.signUpInfo.firstName,
  lastName: state.signUp.signUpInfo.lastName,
  confirmEmail: state.signUp.signUpInfo.confirm_email,
  suggestedEmail: state.signUp.suggestedEmail,
  isEmailSuggested: state.signUp.isEmailSuggested,
  isEmailValidating: state.signUp.isEmailValidating,
  signUpProcess: state.signUp.signUpProcess,
  isThankYouPageVisible: state.signUp.isThankYouPageVisible,
})

const mapDispatchToProps = function (dispatch: any) {
  return {
    validateEmail: bindActionToPromise(dispatch, actions.validateEmail),
    setEmailErrorCode: (code: string) => dispatch(actions.setEmailErrorCode(code)),
    persistSignUpInfo: (data: { type: DocumentTypes } & Partial<SignUpInfo>) =>
      dispatch(actions.persistSignUpInfo(data)),
    setSuggestedEmail: (email: string) => dispatch(actions.setSuggestedEmail(email)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withMediaQueries(SingleSignUp))
